import { Component, OnInit, Optional, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AddProductToCartAttributes } from '@queries/generated';
import { CurrentUserService } from '@services/current-user.service';
import { EcommerceTrackingService } from '@services/ecommerce-tracking.service';
import { ProductCatalogService } from '@services/product-catalog.service';
import { ShoppingCartService } from '@services/shopping-cart.service';
import { StorefrontsService } from '@services/storefronts.service';
import { Fade } from '@util/animations';
import { MessageService } from 'primeng/api';
import { DataView } from 'primeng/dataview';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { tap } from 'rxjs/operators';

@Component({
  selector: 'vnm-product-catalog',
  templateUrl: './product-catalog.component.html',
  styleUrls: ['./product-catalog.component.scss'],
  animations: [Fade]
})
export class ProductCatalogComponent implements OnInit {
  @ViewChild('productCatalog') productCatalog: DataView;

  currentUser$ = this.currentUserService.user$;
  products$ = this.productCatalogService.products$.pipe(tap((products) => this.ecom.viewItemList(products)));
  categories$ = this.productCatalogService.categories$;
  cartItems$ = this.shoppingCartService.items$;
  canCreateOrder$ = this.storefrontsService.canCreateOrder$;

  sortOptions = [
    {
      label: 'name-asc',
      value: 'name'
    },
    {
      label: 'name-desc',
      value: '!name'
    },
    {
      label: 'popularity',
      value: '!ordersCount'
    },
    {
      label: 'price-high',
      value: '!price'
    },
    {
      label: 'price-low',
      value: 'price'
    }
  ];
  sortOrder = -1;
  sortField = 'ordersCount';
  sortKey = '!ordersCount';

  selectedCategories = [];

  constructor(
    private productCatalogService: ProductCatalogService,
    private currentUserService: CurrentUserService,
    private storefrontsService: StorefrontsService,
    private shoppingCartService: ShoppingCartService,
    private messageService: MessageService,
    private translateService: TranslateService,
    private ecom: EcommerceTrackingService,
    @Optional() private dialogConfig: DynamicDialogConfig,
    @Optional() private dialogRef: DynamicDialogRef
  ) {}

  ngOnInit() {
    const cartItems = this.dialogConfig?.data;
    cartItems?.forEach((item) => this.onAddToCart(item));
  }

  onAddToCart(cartItem: AddProductToCartAttributes) {
    this.shoppingCartService.addToCart({ cartItem }).subscribe(
      ({ cartItems }) => {
        const newItem = cartItems.find((item) => item.product.id === cartItem.productId);
        const {
          quantity,
          product: { name }
        } = newItem;

        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('product-catalog.product-added', { name, quantity })
        });

        this.ecom.addToCart(cartItem);
      },
      (error) => {
        this.messageService.add({
          severity: 'error',
          summary: error.message
        });
      }
    );
  }

  onDismiss() {
    this.dialogRef.close();
  }

  onPageCatalog() {
    const catalogElm = this.productCatalog.el.nativeElement;
    catalogElm.scrollIntoView({ behavior: 'smooth' });
  }

  onSortChange({ value }) {
    if (value.indexOf('!') === 0) {
      this.sortOrder = -1;
      this.sortField = value.substring(1, value.length);
    } else {
      this.sortOrder = 1;
      this.sortField = value;
    }
  }

  onRefresh() {
    this.shoppingCartService.refresh();
    this.productCatalogService.refresh();
  }
}
